<template>
  <div class="gs-bumps-step">
    <div v-if="isPostSaleBumpOpen">
      <post-sale-bumps :heading="postSaleBumpDetails.title" :subheading="postSaleBumpDetails.subheading"
        :description="postSaleBumpDetails.description"
        :post-order-video-details="postSaleBumpDetails.post_order_video_details"
        :selected-pricepoint="postSaleBumpDetails.pricing_option" :selected-product="postSaleBumpDetails.product"
        :currency-code="postSaleBumpDetails.currency_code"
        :checkout-page-options="postSaleBumpDetails.checkout_page_options"
        :buynow-text="postSaleBumpDetails.buynow_button_text" :thumbnail="postSaleBumpDetails.thumbnail"
        :thumbnail-img="postSaleBumpDetails.thumbnail_img" :thumbnail-is-square="postSaleBumpDetails.thumbnail_is_square"
        :thumbnail-size="postSaleBumpDetails.thumbnail_size" :is-processing="postSaleBumpDetails.is_processing"
        :bump-type="postSaleBumpDetails.style" :all-products="postSaleBumpDetails.funnelProducts"
        :all-pricepoints="postSaleBumpDetails.productPricePoint" @onClickedClosePreview="clickedClosePreview" />
    </div>
    <section
      class="gs-wizard-card gs-bumps-step__summary"
    >
      <div class="gs-bumps-step__head">
        <h2>Bump offers</h2>
        <gs-button type="button" variant="primary" size="icon" aria-label="Add an order bump" title="Add order bump" @click="openBumpEditor(orderBumpTypeTab, firstPricePointId)">
          <gs-icon name="plus" size="18"></gs-icon>
        </gs-button>
      </div>
      <p>
        Choose a price point, then set whether that price point uses a pre-sell bump, post-sell bump, or no bump.
      </p>
      <div class="gs-bumps-step__price-head">
        <strong>Price points</strong>
        <span>Open one price point to configure its order bump settings.</span>
      </div>
      <div class="gs-bumps-step__price-list">
        <price-point-groups :price-points="bumpRows" v-slot="{ pricePoint }">
        <article
          :key="pricePoint.id"
          class="gs-bumps-step__price-row"
          :class="{ 'is-active': selectedBumpPricePointId === pricePoint.id && showBumpEditor }"
        >
          <button type="button" @click="openBumpEditor(orderBumpTypeTab, pricePoint.id)">
            <span class="gs-bumps-step__status" :class="{ 'is-configured': pricePoint.configured }"></span>
            <div>
              <strong>{{ pricePoint.name }}</strong>
              <small>{{ pricePoint.summary }}</small>
            </div>
            <em>{{ pricePoint.badge }}</em>
            <gs-icon name="chevron-down" size="18"></gs-icon>
          </button>
        </article>
        </price-point-groups>
      </div>
    </section>

    <gs-drawer
      v-model="showBumpEditor"
      size="xl"
      :title="$t('frontend_product_step6_order_bumps')"
      :subtitle="`${activeBumpPricePointName} • ${orderBumpTypeTab === 'presell' ? 'Pre-sell' : 'Post-sell'} bump offer`"
    >
      <div class="gs-bumps-step__editor">
      <presell
        v-show="orderBumpTypeTab === 'presell'"
        ref="presell"
        :model="localModel"
        :selected-price-point-id="selectedBumpPricePointId"
        :is-bump-enabled="selectedBumpHasAnyActiveBump"
        @set-bump-enabled="setSelectedBumpEnabled"
        @update-model="HandleModelUpdate"
      >
        <template v-if="selectedBumpHasAnyActiveBump" slot="after-activation">
          <div class="gs-bumps-step__type-tabs">
            <button
              v-for="tab in orderBumpTypeTabs"
              :key="tab.value"
              type="button"
              :class="{ 'is-active': orderBumpTypeTab === tab.value }"
              @click="handleOrderBumpTypeTabChange(tab.value)"
            >
              {{ tab.title }}
            </button>
          </div>
        </template>
      </presell>
      <postsell
        v-show="orderBumpTypeTab === 'postsell'"
        ref="postsell"
        :model="localModel"
        :selected-price-point-id="selectedBumpPricePointId"
        :is-bump-enabled="selectedBumpHasAnyActiveBump"
        @set-bump-enabled="setSelectedBumpEnabled"
        @preview-post-sale-bump="HandlepreviewPostSaleBump"
        @update-model="HandleModelUpdate"
      >
        <template v-if="selectedBumpHasAnyActiveBump" slot="after-activation">
          <div class="gs-bumps-step__type-tabs">
            <button
              v-for="tab in orderBumpTypeTabs"
              :key="tab.value"
              type="button"
              :class="{ 'is-active': orderBumpTypeTab === tab.value }"
              @click="handleOrderBumpTypeTabChange(tab.value)"
            >
              {{ tab.title }}
            </button>
          </div>
        </template>
      </postsell>
      </div>
    </gs-drawer>
  </div>
</template>

<script>
import ConfigMenu from '@groovepages/gd-ui-kit/components/PluginMenu';
import {
  required,
  minLength,
  requiredIf,
} from 'vuelidate/lib/validators';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import urlParser from 'js-video-url-parser/lib/base';
import PostSaleBumps from '@/components/PostSaleBumps';

import CheckoutPage from '@/assets/images/pages/checkout-page.svg';
import 'js-video-url-parser/lib/provider/wistia';

import Presell from './tabs/bumps/Presell'
import Postsell from './tabs/bumps/Postsell'
import PricePointGroups from '../components/PricePointGroups.vue';

export default {
  name: 'Step6',
  components: {
    CheckoutPage,
    ConfigMenu,
    ImagePicker,
    PostSaleBumps,
    Presell,
    Postsell,
    PricePointGroups
  },
  props: {
    model: {
      required: true,
    },
    funnelProducts: {
      required: true,
    },
  },
  data() {
    return {
      localModel: this.model,
      isPostSaleBumpOpen: false,
      postSaleBumpDetails: {
        title: '',
        description: '',
        post_order_video_details: [],
        style: 1,
        pricing_option: null,
        product: null,
        thumbnail: 0,
        is_processing: false,
        currency_code: '$',
        buynow_button_text: this.$t('frontend_add_to_your_order'),
      },
      previewOrderBumps: false,
      offerBumpComponentKey: 1,
      orderBumpTypeTabs: [
        {
          title: this.$t('frontend_product_step6_tab_presell'),
          value: 'presell',
        },
        {
          title: this.$t('frontend_product_step6_tab_postsell'),
          value: 'postsell',
        },
      ],
      orderBumpTypeTab: 'presell',
      showBumpEditor: false,
      selectedBumpPricePointId: null,
      products: null,
      pricePoints: null,
      errorTabs: [],
    };
  },
  computed: {
    firstPricePointId() {
      const pricePoints = this.localModel.pricing_points || [];
      return pricePoints[0] ? pricePoints[0].id : null;
    },
    activeBumpPricePointName() {
      const pricePoint = (this.localModel.pricing_points || []).find((item) => item.id === this.selectedBumpPricePointId);
      return pricePoint ? pricePoint.name : 'Select a price point';
    },
    selectedBumpPricePoint() {
      return (this.localModel.pricing_points || []).find((item) => item.id === this.selectedBumpPricePointId) || null;
    },
    isSelectedPresellEnabled() {
      return !!(this.selectedBumpPricePoint && Number(this.selectedBumpPricePoint.offer_bump_enable));
    },
    isSelectedPostsellEnabled() {
      return !!(
        this.selectedBumpPricePoint &&
        this.selectedBumpPricePoint.post_sale_order_bump_options &&
        Number(this.selectedBumpPricePoint.post_sale_order_bump_options.post_order_bump_enable)
      );
    },
    selectedBumpHasAnyActiveBump() {
      return this.isSelectedPresellEnabled || this.isSelectedPostsellEnabled;
    },
    bumpRows() {
      return (this.localModel.pricing_points || []).map((pricePoint) => {
        const presellCount = (pricePoint.offer_bump_options || []).length;
        const postsellEnabled = pricePoint.post_sale_order_bump_options &&
          pricePoint.post_sale_order_bump_options.post_order_bump_enable;
        const configured = this.orderBumpTypeTab === 'presell' ? presellCount > 0 : !!postsellEnabled;

        return {
          id: pricePoint.id,
          name: pricePoint.name,
          is_pinned: pricePoint.is_pinned,
          sort_order: pricePoint.sort_order,
          configured,
          badge: configured ? 'Configured' : 'Not configured',
          summary: this.orderBumpTypeTab === 'presell'
            ? `${presellCount} pre-sell ${presellCount === 1 ? 'offer' : 'offers'}`
            : (postsellEnabled ? 'Post-sell bump active' : 'Post-sell bump disabled'),
        };
      });
    },
  },
  validations: {
    localModel: {
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          offer_bump_options: {
            $each: {
              heading_line: {
                required: requiredIf((object, value) => {
                  if (object) {
                    return true;
                  }

                  return false;
                }),
              },
              description: {
                required: requiredIf((object, value) => {
                  if (object) {
                    return true;
                  }

                  return false;
                }),
              },
            },
          },
          post_sale_order_bump_options: {
            post_order_heading_line: {
              required: requiredIf((object) => {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1
                ) {

                  return true;
                }

                return false;
              }),
            },
            post_order_subheading: {
              required: requiredIf((object) => {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1
                ) {
                  return true;
                }
                return false;
              }),
            },
            post_order_description: {
              required: requiredIf((object) => {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1
                ) {
                  return true;
                }
                return false;
              }),
            },
            post_order_product_id: {
              isValidProductId(value, object) {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1 &&
                  (!value || value == 0)
                ) {
                  return false;
                }
                return true;
              },
            },
            post_order_price_point_id: {
              isValidPricePointtId(value, object) {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1 &&
                  (!value || value == 0)
                ) {
                  return false;
                }
                return true;
              },
            },
            post_order_video_details: {
              url: {
                required: requiredIf((object) => {
                  if (
                    object.post_order_show_video &&
                    object.post_order_show_video == true
                  ) {
                    return true;
                  }
                  return false;
                }),
                isValidPostSaleVideoUrl(value, object) {
                  if (
                    object.post_order_show_video &&
                    object.post_order_show_video == true
                  ) {
                    const videoInfo = urlParser.parse(value);

                    if (videoInfo != undefined) {
                      return true;
                    }
                    const brandAltName = this.$store.state.brandData.brandAltName.toLowerCase();
                    const videoAppTitle = this.whitelabelAppData('video').title.toLowerCase();
                    const expression = new RegExp(`(http:|https:|)\/\/?(app\\.${brandAltName}\\.com)\/${videoAppTitle}\/videopage\/[A-Za-z0-9]*\/[A-Za-z0-9]*`, 'g');
                    const regex = new RegExp(expression);
                    if (value.match(regex)) {
                      return true;
                    }
                    return false;
                  }
                  return true;
                },
              },
            },
          },
        },
      },
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  methods: {
    HandleModelUpdate(newVal) {
      this.localModel = newVal;
    },
    openBumpEditor(type, pricePointId) {
      this.orderBumpTypeTab = type;
      this.selectedBumpPricePointId = pricePointId || this.firstPricePointId;
      if (this.selectedBumpHasAnyActiveBump) {
        this.activateSelectedBumpType(type);
      }
      this.showBumpEditor = true;
    },
    clickedClosePreview() {
      this.isPostSaleBumpOpen = false;
    },
    HandlepreviewPostSaleBump(postSaleOrderBumpOptions, funnelProducts, postSaleProductPricePoint) {
      this.previewPostSaleBump(postSaleOrderBumpOptions, funnelProducts, postSaleProductPricePoint);
    },
    previewPostSaleBump(post_sale_order_bump_options, products, pricePoints) {
      this.postSaleBumpDetails = {
        title: post_sale_order_bump_options.post_order_heading_line,
        subheading: post_sale_order_bump_options.post_order_subheading,
        description: post_sale_order_bump_options.post_order_description,
        post_order_video_details:
          post_sale_order_bump_options.post_order_video_details,
        style: post_sale_order_bump_options.post_order_style_id,
        product: post_sale_order_bump_options.post_order_product_id,
        pricing_option: post_sale_order_bump_options.post_order_price_point_id,
        thumbnail: post_sale_order_bump_options.post_order_display_thumbnail,
        thumbnail_img: post_sale_order_bump_options.post_order_thumbnail_image,
        thumbnail_is_square: post_sale_order_bump_options.post_order_is_square,
        thumbnail_size: post_sale_order_bump_options.post_order_thumbnail_size,
        currency_code: '$',
        buynow_button_text: this.$t('frontend_yes_add_to_my_order'),
        funnelProducts: products,
        productPricePoint: pricePoints,
        checkout_page_options: this.localModel.checkout_page_options,
      };

      this.isPostSaleBumpOpen = true;
    },
  
    funnelName(id) {
      const funnel = this.funnels.find((funnel) => funnel.id === id);
      return funnel.name;
    },

    productName(funnelid, id) {
      const funnel = this.funnels.find((funnel) => funnel.id === funnelid);
      const product = funnel.products.find((product) => product.id === id);
      return product.name;
    },

    pricepointName(funnelid, productid, id) {
      const funnel = this.funnels.find((funnel) => funnel.id === funnelid);
      const product = funnel.products.find(
        (product) => product.id === productid
      );
      const pricepoint = product.price_points.find(
        (pricePoint) => pricePoint.id === id
      );
      return pricepoint.name;
    },

    getHex() {
      let rgb = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(`--vs-${this.color}`);
      rgb = rgb.split(',');
      return `#${(
        (1 << 24) +
        (Number(rgb[0]) << 16) +
        (Number(rgb[1]) << 8) +
        Number(rgb[2])
      )
        .toString(16)
        .slice(1)}`;
    },

    setValue(value, index) {
      this.localModel.pricing_points[index].style_id = value;
    },

  

    handleOrderBumpTypeTabChange(newTab) {
      this.orderBumpTypeTab = newTab;
      if (this.selectedBumpHasAnyActiveBump) {
        this.activateSelectedBumpType(newTab);
      }
    },
    setSelectedBumpEnabled(payload) {
      const enabled = !!(payload && payload.enabled);
      const type = payload && payload.type ? payload.type : this.orderBumpTypeTab;
      if (!enabled) {
        this.setAllBumpTypesEnabled(false);
        return;
      }

      this.activateSelectedBumpType(type);
    },
    selectedBumpPricePointIndex() {
      return (this.localModel.pricing_points || []).findIndex((pricePoint) => pricePoint.id === this.selectedBumpPricePointId);
    },
    ensurePostSaleBumpOptions(pricePoint) {
      if (!pricePoint.post_sale_order_bump_options) {
        this.$set(pricePoint, 'post_sale_order_bump_options', {});
      }

      if (!pricePoint.post_sale_order_bump_options.post_order_video_details) {
        this.$set(pricePoint.post_sale_order_bump_options, 'post_order_video_details', {});
      }
    },
    ensureOfferBumpOptions(pricePoint) {
      if (!pricePoint.offer_bump_options) {
        this.$set(pricePoint, 'offer_bump_options', []);
      }
    },
    activateSelectedBumpType(type) {
      const index = this.selectedBumpPricePointIndex();
      if (index < 0) {
        return;
      }

      const pricePoint = this.localModel.pricing_points[index];
      if (type === 'postsell') {
        this.ensurePostSaleBumpOptions(pricePoint);
        this.$set(pricePoint.post_sale_order_bump_options, 'post_order_bump_enable', 1);
        return;
      }

      this.ensureOfferBumpOptions(pricePoint);
      this.$set(pricePoint, 'offer_bump_enable', 1);
    },
    setAllBumpTypesEnabled(enabled) {
      const index = this.selectedBumpPricePointIndex();
      if (index < 0) {
        return;
      }

      const pricePoint = this.localModel.pricing_points[index];
      this.$set(pricePoint, 'offer_bump_enable', enabled ? 1 : 0);
      this.ensurePostSaleBumpOptions(pricePoint);
      this.$set(pricePoint.post_sale_order_bump_options, 'post_order_bump_enable', enabled ? 1 : 0);
    },

    validate() {
      this.$refs.presell?.$v?.$touch();
      this.$refs.postsell?.$v?.$touch();
      this.errorTabs = this.getErrorTabs();
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
    getErrorTabs() {
      const invalidTabs = [];

      if (this.$refs.presell?.$v?.$invalid) {
        invalidTabs.push('presell');
      }

      if (this.$refs.postsell?.$v?.$invalid) {
        invalidTabs.push('postsell');
      }

      return invalidTabs;
    },
  },
};
</script>
<style>
.gs-bumps-step {
  display: flex;
  flex-direction: column;
  gap: 14px;
}


.gs-bumps-step__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gs-bumps-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}



.gs-bumps-step__price-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 18px 0 10px;
}

.gs-bumps-step__price-head strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-bumps-step__price-head span {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.4;
}

.gs-bumps-step__price-list {
  display: grid;
  gap: 10px;
}

.gs-bumps-step__price-row {
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-bumps-step__price-row.is-active {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-bumps-step__price-row button {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto 18px;
  gap: 14px;
  align-items: center;
  width: 100%;
  min-height: 72px;
  padding: 14px 16px;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-bumps-step__status {
  width: 12px;
  height: 12px;
  border: 2px solid var(--gs-border);
  border-radius: 50%;
}

.gs-bumps-step__status.is-configured {
  background: var(--gs-success);
  border-color: var(--gs-success);
}

.gs-bumps-step__price-row strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-bumps-step__price-row small {
  display: block;
  margin-top: 3px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}

.gs-bumps-step__price-row em {
  padding: 8px 12px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-bumps-step__price-row.is-active em {
  color: var(--gs-active);
  background: var(--gs-active-soft);
}

.gs-bumps-step__type-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 26px 0 18px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-bumps-step__type-tabs button {
  min-height: 38px;
  padding: 0 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 600;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-bumps-step__type-tabs button.is-active {
  color: var(--gs-active);
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}


</style>
